import React, { useMemo } from 'react'
import { Button, Avatar, AvatarFallback, Skeleton, TwEmoji } from '@t-one-internal/design-system'
import { DataGrid } from '@t-one-internal/design-system/lib/components/ui/data-grid'
import type { ColumnDef, PaginationState } from '@tanstack/react-table'
import { useRecognitionStore } from '../../../stores/recognitionStore'
import { useLeaderboardData } from '../../../hooks/useLeaderboardData'
import type { LeaderboardEntry } from '../../../types/recognition'

import { hashValueColor } from '../utils/valueColors'

const PERIODS = [
  { id: 'week' as const, label: 'Week' },
  { id: 'month' as const, label: 'Month' },
  { id: 'quarter' as const, label: 'Quarter' },
  { id: 'year' as const, label: 'Year' },
]

const PODIUM_COLORS = ['#F5A623', '#C0C0C0', '#CD7F32']
const MEDAL_ICONS = ['🥇', '🥈', '🥉']

const LeaderboardView: React.FC = () => {
  useLeaderboardData()

  const leaderboard = useRecognitionStore((s) => s.leaderboard)
  const leaderboardLoading = useRecognitionStore((s) => s.leaderboardLoading)
  const period = useRecognitionStore((s) => s.leaderboardPeriod)
  const setPeriod = useRecognitionStore((s) => s.setLeaderboardPeriod)
  const leaderboardTotal = useRecognitionStore((s) => s.leaderboardTotal)
  const leaderboardPage = useRecognitionStore((s) => s.leaderboardPage)
  const leaderboardPageSize = useRecognitionStore((s) => s.leaderboardPageSize)
  const setLeaderboardPage = useRecognitionStore((s) => s.setLeaderboardPage)
  const setLeaderboardPageSize = useRecognitionStore((s) => s.setLeaderboardPageSize)

  // Pagination state for DataGrid derived directly from store
  const pagination: PaginationState = {
    pageIndex: leaderboardPage - 1,
    pageSize: leaderboardPageSize,
  }

  const pageCount = Math.max(1, Math.ceil(leaderboardTotal / leaderboardPageSize))

  const handlePaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    if (newPagination.pageSize !== leaderboardPageSize) {
      setLeaderboardPageSize(newPagination.pageSize)
    } else if (newPagination.pageIndex !== leaderboardPage - 1) {
      setLeaderboardPage(newPagination.pageIndex + 1)
    }
  }

  // DataGrid column definitions - matching the existing table pattern
  const columns: ColumnDef<LeaderboardEntry>[] = useMemo(() => [
    {
      id: 'rank',
      accessorKey: 'rank',
      header: 'Rank',
      size: 70,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const rank = row.original.rank
        return (
          <span className={`text-sm font-bold ${rank <= 3 ? 'text-amber-500' : 'text-muted-foreground'}`}>
            #{rank}
          </span>
        )
      },
    },
    {
      id: 'employee',
      accessorFn: (row) => row.employee.name,
      header: 'Employee',
      size: 250,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const entry = row.original
        return (
          <div className="flex items-center gap-2.5">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="text-xs font-bold bg-primary/20 text-primary">
                {entry.employee.initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-semibold text-foreground">{entry.employee.name}</p>
              <p className="text-[11px] text-muted-foreground">{entry.employee.dept}</p>
            </div>
          </div>
        )
      },
    },
    {
      id: 'count',
      accessorKey: 'count',
      header: 'Recognitions',
      size: 120,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <span className="text-sm font-bold text-foreground">{row.original.count}</span>
      ),
    },
    {
      id: 'points',
      accessorKey: 'points',
      header: 'Points',
      size: 100,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-amber-500">{row.original.points.toLocaleString()}</span>
      ),
    },
    {
      id: 'xp',
      accessorKey: 'xp',
      header: 'XP',
      size: 100,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-violet-500">{row.original.xp.toLocaleString()}</span>
      ),
    },
    {
      id: 'topValue',
      accessorKey: 'topValue',
      header: 'Top Value',
      size: 130,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const value = row.original.topValue
        if (!value) return <span className="text-xs text-muted-foreground">—</span>
        const vc = hashValueColor(value)
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold w-fit"
            style={{ color: vc.text, backgroundColor: vc.bg }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: vc.text }} />
            {value}
          </span>
        )
      },
    },
  ], [])

  // Loading state — skeleton matching the leaderboard layout
  if (leaderboardLoading) {
    return (
      <div className="p-6 overflow-auto h-full">
        {/* Period selector skeleton */}
        <div className="flex justify-end mb-6">
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {PERIODS.map((p) => (
              <Skeleton key={p.id} className="h-7 w-16 rounded-md" />
            ))}
          </div>
        </div>
        {/* Podium skeleton */}
        <div className="flex justify-center items-end gap-6 mb-10 px-10">
          {[100, 140, 80].map((h, i) => (
            <div key={i} className="text-center flex-1">
              <Skeleton className={`${i === 1 ? 'w-16 h-16' : 'w-12 h-12'} rounded-full mx-auto`} />
              <Skeleton className="h-4 w-20 mx-auto mt-2.5" />
              <Skeleton className="h-3 w-14 mx-auto mt-1" />
              <Skeleton className="rounded-t-xl mt-3 w-full" style={{ height: h }} />
            </div>
          ))}
        </div>
        {/* Table rows skeleton */}
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 px-2">
              <Skeleton className="h-5 w-8" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (leaderboard.length === 0) {
    return (
      <div className="p-6 overflow-auto h-full">
        {/* Period Selector */}
        <div className="flex justify-end mb-6">
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {PERIODS.map((p) => (
              <Button
                key={p.id}
                variant={period === p.id ? 'default' : 'ghost'}
                size="sm"
                className="text-xs h-7"
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center py-12 min-h-[400px]">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <img
                src="/assets/EmptyTable2.png"
                alt="No leaderboard data"
                className="w-32 h-32 object-contain opacity-60"
              />
            </div>
            <p className="text-lg font-medium text-gray-900 mb-2">No leaderboard data yet</p>
            <p className="text-sm text-gray-500">Start recognising colleagues to see the leaderboard come alive!</p>
          </div>
        </div>
      </div>
    )
  }

  // Podium: always show the first 3 people from the sorted leaderboard.
  // Each person keeps their actual rank (may be tied). Visual order: [2nd, 1st, 3rd].
  const showPodium = leaderboard.length >= 3 && leaderboardPage === 1
  // positions 0,1,2 map to the first 3 entries in the leaderboard array
  const top3 = leaderboard.slice(0, 3)
  return (
    <div className="p-6 overflow-auto h-full">
      {/* Period Selector */}
      <div className="flex justify-end mb-6">
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {PERIODS.map((p) => (
            <Button
              key={p.id}
              variant={period === p.id ? 'default' : 'ghost'}
              size="sm"
              className="text-xs h-7"
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Podium — exactly 3 people: visual order [2nd, 1st, 3rd] */}
      {showPodium && (() => {
        const podiumItems = [
          { entry: top3[1], color: PODIUM_COLORS[1], medal: MEDAL_ICONS[1], avatar: 'w-12 h-12 text-sm', h: 100, nameSize: 'text-sm' },
          { entry: top3[0], color: PODIUM_COLORS[0], medal: MEDAL_ICONS[0], avatar: 'w-16 h-16 text-base', h: 140, nameSize: 'text-base' },
          { entry: top3[2], color: PODIUM_COLORS[2], medal: MEDAL_ICONS[2], avatar: 'w-11 h-11 text-xs', h: 80, nameSize: 'text-sm' },
        ]
        return (
          <div className="flex justify-center items-end gap-6 mb-10 px-10 max-w-2xl mx-auto overflow-hidden">
            {podiumItems.map(({ entry, color, medal, avatar, h, nameSize }, idx) => (
              <div key={`podium-${idx}`} className="text-center flex-1 min-w-0">
                <Avatar
                  className={`${avatar} mx-auto shadow-lg`}
                  style={{
                    background: `linear-gradient(135deg, ${color}, ${color}88)`,
                    boxShadow: `0 0 20px ${color}44`,
                  }}
                >
                  <AvatarFallback className="font-bold text-white bg-transparent">
                    {entry.employee.initials}
                  </AvatarFallback>
                </Avatar>
                <p className={`font-bold text-foreground mt-2.5 ${nameSize} truncate`}>
                  {entry.employee.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">{entry.employee.dept}</p>
                <div
                  className="rounded-t-xl mt-3 flex flex-col items-center justify-center border border-b-0"
                  style={{
                    height: h,
                    background: `linear-gradient(180deg, ${color}15, transparent)`,
                    borderColor: `${color}20`,
                  }}
                >
                  <TwEmoji emoji={medal} size={28} />
                  <span className="text-xl font-bold text-foreground">{entry.count}</span>
                  <span className="text-[11px] text-muted-foreground">recognitions</span>
                </div>
              </div>
            ))}
          </div>
        )
      })()}

      {/* Leaderboard Table with Pagination */}
      <DataGrid
        columns={columns}
        data={leaderboard}
        loading={leaderboardLoading}
        manualPagination={true}
        pageCount={pageCount}
        totalCount={leaderboardTotal}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        enableRowSelection={false}
        enableColumnResizing={false}
        enableSorting={false}
        showToolbar={false}
        showPagination={true}
        showFloatingBar={false}
        rowDensity="normal"
        getRowId={(row: LeaderboardEntry) => `${row.employee.id}-${row.employee.name}`}
      />
    </div>
  )
}

export default LeaderboardView
